import React, { useState } from 'react'
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  CircularProgress
} from '@mui/material'
import { Visibility, VisibilityOff, HomeOutlined, ArrowBack, Email } from '@mui/icons-material'
import { useMediaQuery, useTheme } from '@mui/system'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as yup from 'yup'

import ForgotPasswordDialog from '../modals/ForgotPasswordDialog'
import GoogleIcon from '../../assets/devicon--google.svg'
import { login } from '../../store/actions'
import { LoginAuthProvider } from '../../types/auth'
import Logo from '../../layout/MainLayout/LogoSection/Logo'
import PlatformScreenforLogin from '../../assets/PlatformScreenforLogin.webp'

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup
    .string()
    .min(8, 'Password should be at least 8 characters')
    .max(50, 'Password should not be contained greater then 8 characters')
    .required('Password is required')
})

export default function LoginPage() {
  const theme = useTheme()
  const dispatch: any = useDispatch()
  const navigate: any = useNavigate()
  const isShortHeight = useMediaQuery('(max-height: 720px)')

  const client = useSelector((state: any) => state.apolloClient.client)
  const { isLoading } = useSelector((state: any) => state.customization)

  const [showPassword, setShowPassword] = useState(false)
  const [open, setOpen] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false)

  const handleClickShowPassword = () => setShowPassword(!showPassword)

  const handleLoginOrSignUp = async (provider: LoginAuthProvider, args?: { email: string; password: string }) => {
    dispatch(login(provider, client, args, navigate) as any)
  }

  const handleContinueWithEmail = () => {
    setShowEmailForm(true)
  }

  const urlParams = new URLSearchParams(window.location.search)
  const emailVerified = urlParams.get('emailVerified')
  const userEmailFromURL = urlParams.get('email') || '' // Fallback to 'test' if no email param

  const formik = useFormik({
    initialValues: { email: emailVerified ? userEmailFromURL : '', password: '' },
    validationSchema,
    onSubmit: (values) => handleLoginOrSignUp(LoginAuthProvider.Email, values)
  })

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {/* Background Image */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${PlatformScreenforLogin})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px)',
          transform: 'scale(1)'
        }}
      />

      {/* Login Card */}
      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          zIndex: 10,
          py: { xs: 1, sm: 2 }
        }}
      >
        <Paper
          elevation={24}
          sx={{
            p: isShortHeight ? { xs: 2.5, sm: 3, md: 3 } : { xs: 2.5, sm: 3, md: 4 },
            borderRadius: '20px !important',
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxHeight: isShortHeight ? 'calc(100vh - 20px)' : { xs: 'calc(100vh - 32px)', sm: 'calc(100vh - 48px)' },
            overflowY: isShortHeight ? 'visible' : 'auto'
          }}
        >
          {/* Logo/Brand */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '456px',
              mx: 'auto',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {/* Back Button */}
            <Box
              sx={{
                position: 'absolute',
                top: { xs: 20, md: 36 },
                left: { xs: 20, md: 36 },
                display: 'flex',
                alignItems: 'center',
                mb: { xs: 1.5, sm: 2 }
              }}
            >
              <IconButton
                onClick={() => (showEmailForm ? setShowEmailForm(false) : navigate('/'))}
                sx={{
                  p: 0.5,
                  mr: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                {showEmailForm ? (
                  <ArrowBack sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                ) : (
                  <HomeOutlined sx={{ fontSize: { xs: 20, sm: 22, md: 24 } }} />
                )}
              </IconButton>
            </Box>
            {/* Logo */}
            <Box
              sx={{
                mb: { xs: 1, sm: 1.5, md: 2 },
                marginRight: isShortHeight ? { xs: 2, sm: 1, md: 3 } : { xs: 2, sm: 1, md: 0 },
                width: isShortHeight ? { xs: '80px', sm: '88px', md: '70px' } : { xs: '80px', sm: '88px', md: '96px' },
                height: isShortHeight ? { xs: '31px', sm: '34px', md: '28px' } : { xs: '31px', sm: '34px', md: '37px' }
              }}
            >
              <Link to="/">
                <Logo />
              </Link>
            </Box>

            {!showEmailForm ? (
              <Box sx={{ width: '100%' }}>
                {/* Title */}
                <Typography
                  sx={{
                    mb: { xs: 1, sm: 1.5 },
                    maxWidth: '100%',
                    lineHeight: { xs: '28px', sm: '32px', md: '36px' },
                    fontFamily: 'Inter-Bold !important',
                    fontSize: { xs: '20px', sm: '24px', md: '28px' },
                    letterSpacing: '-0.02em',
                    color: '#000000',
                    textAlign: 'center'
                  }}
                >
                  Log In within seconds
                </Typography>

                <Typography
                  sx={{
                    mb: { xs: 2.5, sm: 3, md: 4 },
                    fontSize: { xs: '13px', sm: '14px', md: '15px' },
                    color: 'text.secondary',
                    textAlign: 'center'
                  }}
                >
                  Select an option to continue
                </Typography>

                {/* Continue With Email Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleContinueWithEmail}
                  sx={{
                    height: { xs: 40, sm: 44, md: 48 },
                    mb: { xs: 1.5, sm: 2 },
                    border: '1px solid #DBDBDB',
                    color: 'text.primary',
                    textTransform: 'none',
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 500,
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <Email sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  Continue With Email
                </Button>

                {/* Log In With Google Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleLoginOrSignUp(LoginAuthProvider.Google)}
                  sx={{
                    height: { xs: 40, sm: 44, md: 48 },
                    border: '1px solid #DBDBDB',
                    color: 'text.primary',
                    textTransform: 'none',
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 500,
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <Box
                    component="img"
                    src={GoogleIcon}
                    sx={{ height: { xs: 18, sm: 20 }, width: { xs: 18, sm: 20 } }}
                  />
                  Login With Google
                </Button>

                {/* Footer Text */}
                <Typography
                  sx={{
                    mt: { xs: 2.5, sm: 3 },
                    fontSize: { xs: '11px', sm: '12px' },
                    color: 'text.secondary',
                    textAlign: 'center',
                    lineHeight: 1.6
                  }}
                >
                  By continuing you agree to our{' '}
                  <Link to="/advertiser-guidelines" style={{ color: theme.palette.primary.main }}>
                    Advertiser Guidelines
                  </Link>
                  ,{' '}
                  <Link to="/terms" style={{ color: theme.palette.primary.main }}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" style={{ color: theme.palette.primary.main }}>
                    Privacy Policy
                  </Link>
                  .
                </Typography>
              </Box>
            ) : (
              // Email Form View
              <Box sx={{ width: '100%' }}>
                {/* Title */}
                <Typography
                  variant="h3"
                  sx={{
                    mb: { xs: 0.5, sm: 1, md: 1.5 },
                    lineHeight: { xs: '28px', sm: '36px', md: '44px', lg: '52px' },
                    fontFamily: 'Inter-Bold !important',
                    fontSize: isShortHeight
                      ? { xs: '24px', sm: '32px', md: '38px' }
                      : { xs: '24px', sm: '32px', md: '48px' },
                    textAlign: 'center',
                    color: '#000'
                  }}
                >
                  Welcome
                </Typography>

                <Typography
                  sx={{
                    mb: { xs: 1.5, sm: 2, md: 2.5 },
                    fontFamily: 'Inter-Regular !important',
                    fontSize: { xs: '12px', sm: '14px', md: '16px' },
                    textAlign: 'center',
                    color: '#000'
                  }}
                >
                  Enter your login credentials
                </Typography>

                {/* Form container */}
                <Box sx={{ width: '100%', maxWidth: '456px' }} component="form" onSubmit={formik?.handleSubmit}>
                  {/* Email */}
                  <Box sx={{ width: '100%', mb: { xs: 1, sm: 1.5 } }}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '13px', md: '14px' }
                      }}
                    >
                      Email
                    </Typography>
                    <TextField
                      fullWidth
                      id="email"
                      name="email"
                      placeholder="Enter email"
                      value={formik?.values?.email}
                      onChange={formik?.handleChange}
                      onBlur={formik?.handleBlur}
                      error={formik?.touched?.email && Boolean(formik?.errors?.email)}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: isShortHeight ? { xs: 40, sm: 44, md: 44 } : { xs: 40, sm: 44, md: 48 }
                        }
                      }}
                    />
                    <Typography
                      sx={{
                        minHeight: { xs: '16px', sm: '18px' },
                        mt: '2px',
                        fontSize: { xs: '10px', sm: '11px', md: '12px' },
                        color: theme.palette.red?.[500] || '#ef4444'
                      }}
                    >
                      {formik?.touched?.email && formik?.errors?.email}
                    </Typography>
                  </Box>

                  {/* Password */}
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        fontWeight: 500,
                        fontSize: { xs: '12px', sm: '13px', md: '14px' }
                      }}
                    >
                      Password
                    </Typography>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="**********"
                      value={formik?.values?.password}
                      onChange={formik?.handleChange}
                      onBlur={formik?.handleBlur}
                      error={formik?.touched?.password && Boolean(formik?.errors?.password)}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: isShortHeight ? { xs: 40, sm: 44, md: 44 } : { xs: 40, sm: 44, md: 48 }
                        }
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Typography
                      sx={{
                        minHeight: { xs: '16px', sm: '18px' },
                        mt: '2px',
                        fontSize: { xs: '10px', sm: '11px', md: '12px' },
                        color: theme.palette.red?.[500] || '#ef4444'
                      }}
                    >
                      {formik?.touched?.password && formik?.errors?.password}
                    </Typography>
                  </Box>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      textAlign: 'right',
                      mb: { xs: 1.5, sm: 2, md: 2.5 },
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: { xs: '12px', sm: '13px', md: '14px' },
                      color: theme.palette.primary.main
                    }}
                    onClick={() => setOpen(!open)}
                  >
                    Forgot Password?
                  </Typography>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    disabled={isLoading}
                    variant="contained"
                    sx={{
                      height: { xs: 40, sm: 44, md: 48 },
                      textTransform: 'none',
                      fontSize: { xs: '13px', sm: '14px', md: '16px' },
                      fontWeight: 600,
                      '&.Mui-disabled': {
                        backgroundColor: `${theme.palette.primary.main}66`,
                        color: 'white',
                        pointerEvents: 'none'
                      }
                    }}
                  >
                    {isLoading && (
                      <CircularProgress
                        size={20}
                        sx={{
                          color: 'white',
                          marginRight: '20px'
                        }}
                      />
                    )}
                    Login
                  </Button>

                  {open && <ForgotPasswordDialog open={open} handleClose={() => setOpen(false)} />}
                </Box>

                {/* Link */}
                <Box sx={{ textAlign: 'center', width: '100%', mt: { xs: 1.5, sm: 2, md: 2.5 } }}>
                  <Typography sx={{ fontSize: { xs: '12px', sm: '13px', md: '14px' } }}>
                    No account yet?{' '}
                    <Link style={{ color: theme.palette.primary.main, fontWeight: 500 }} to="/register">
                      Create Account
                    </Link>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}
